/**
 * useAutoDrafting - Hook for managing auto-drafting state and logic
 */
import { useState, useRef, useEffect, useCallback } from 'react';

// Action words that indicate document CREATION (not questions about documents)
const CREATION_VERBS_NO = [
  'lag', 'skriv', 'opprett', 'generer', 'formuler', 'utform', 'lage', 'skriv ut',
  'forbered', 'sett opp', 'utarbeid', 'ferdigstill', 'forfatt', 'utfør'
];

const CREATION_VERBS_EN = [
  'create', 'draft', 'write', 'generate', 'prepare', 'make', 'compose',
  'produce', 'formulate', 'draw up', 'put together', 'build', 'construct'
];

// Document types - more comprehensive list
const DOCUMENT_TYPES = [
  // Norwegian
  'kontrakt', 'avtale', 'brev', 'dokument', 'utkast', 'notat', 'policy', 'mal',
  'arbeidsavtale', 'ansettelsesavtale', 'oppsigelse', 'fullmakt', 'testament', 'leiekontrakt',
  'aksjonæravtale', 'samarbeidsavtale', 'taushetsavtale', 'nda', 'intensjonsavtale',
  'kjøpsavtale', 'salgsavtale', 'husleiekontrakt', 'franchiseavtale', 'lisensavtale',
  // English
  'contract', 'agreement', 'letter', 'memo', 'template', 'document', 'draft',
  'employment contract', 'employment agreement', 'work contract', 'service agreement',
  'nda', 'non-disclosure', 'confidentiality agreement', 'shareholder agreement',
  'partnership agreement', 'lease agreement', 'rental agreement', 'sales agreement',
  'purchase agreement', 'consulting agreement', 'freelance contract', 'terms of service',
  'privacy policy', 'terms and conditions', 'license agreement', 'franchise agreement'
];

// Question words that indicate it's NOT a creation request
const QUESTION_INDICATORS = [
  'hva er', 'hva betyr', 'forklar', 'fortell', 'hva med', 'hvilke', 'hvorfor', 'hvordan',
  'what is', 'what are', 'explain', 'tell me', 'describe', 'what about', 'which', 'why', 'how',
  'kan du forklare', 'can you explain', 'summary', 'summarize', 'oppsummer', 'key clause',
  'nøkkelklausul', 'main point', 'hovedpunkt', 'breakdown', 'analyse', 'analyze'
];

// Agents that always trigger drafting
const DRAFTING_AGENTS = ['contract_analyst', 'document_drafting', 'legal_writer'];

export const useAutoDrafting = () => {
  // Auto Drafting preference - ON by default
  const [autoDrafting, setAutoDrafting] = useState(() => {
    const saved = localStorage.getItem('lexy_auto_drafting');
    return saved !== null ? JSON.parse(saved) : true;
  });
  
  // Draft state
  const [currentDraftId, setCurrentDraftId] = useState(null);
  const [activeDraftId, setActiveDraftId] = useState(null);
  const [streamingToDraft, setStreamingToDraft] = useState(false);
  const [draftStreamingContent, setDraftStreamingContent] = useState('');
  
  // Refs for streaming
  const draftContentRef = useRef('');
  const draftIdRef = useRef(null);
  const streamingToDraftRef = useRef(false);

  // Save preference to localStorage
  useEffect(() => {
    localStorage.setItem('lexy_auto_drafting', JSON.stringify(autoDrafting));
  }, [autoDrafting]);

  // Check if content is a document CREATION request (not a question about documents)
  const isDocumentRequest = useCallback((content) => {
    if (!content) return false;
    const lowerContent = content.toLowerCase().trim();
    
    // Check if it has a creation verb + document type combination
    const hasCreationVerb = [...CREATION_VERBS_NO, ...CREATION_VERBS_EN].some(verb => 
      lowerContent.includes(verb)
    );
    
    const hasDocumentType = DOCUMENT_TYPES.some(type => 
      lowerContent.includes(type)
    );
    
    // If it has BOTH creation verb AND document type, it's a creation request
    // This takes priority - even if phrased as a question like "Can you draft...?"
    if (hasCreationVerb && hasDocumentType) {
      return true;
    }
    
    // If it's ONLY asking about documents (questions without creation intent)
    // Check for question indicators that suggest it's NOT a creation request
    const hasQuestionIndicator = QUESTION_INDICATORS.some(q => lowerContent.includes(q));
    
    // If it has question indicators but no creation verb, it's just a question
    if (hasQuestionIndicator && !hasCreationVerb) {
      return false;
    }
    
    return false;
  }, []);

  // Check if selected agent is a drafting agent
  const isDraftingAgent = useCallback((selectedAgent) => {
    if (!selectedAgent) return false;
    return DRAFTING_AGENTS.some(agentType => 
      selectedAgent.id?.toLowerCase().includes(agentType) ||
      selectedAgent.name?.toLowerCase().includes(agentType)
    );
  }, []);

  // Determine if auto-draft should be triggered
  const shouldAutoDraft = useCallback((content, selectedAgent) => {
    return autoDrafting && (isDocumentRequest(content) || isDraftingAgent(selectedAgent));
  }, [autoDrafting, isDocumentRequest, isDraftingAgent]);

  // Reset draft streaming state
  const resetDraftStreaming = useCallback(() => {
    draftContentRef.current = '';
    draftIdRef.current = null;
    streamingToDraftRef.current = false;
    setStreamingToDraft(false);
    setDraftStreamingContent('');
  }, []);

  // Start draft streaming
  const startDraftStreaming = useCallback(() => {
    streamingToDraftRef.current = true;
    setStreamingToDraft(true);
  }, []);

  // Append content to draft
  const appendDraftContent = useCallback((content) => {
    draftContentRef.current += content;
    setDraftStreamingContent(draftContentRef.current);
  }, []);

  // Finalize draft with ID
  const finalizeDraft = useCallback((draftId) => {
    draftIdRef.current = draftId;
    setCurrentDraftId(draftId);
    setActiveDraftId(draftId);
    streamingToDraftRef.current = false;
    setStreamingToDraft(false);
  }, []);

  // Toggle auto-drafting
  const toggleAutoDrafting = useCallback(() => {
    setAutoDrafting(prev => !prev);
  }, []);

  return {
    // State
    autoDrafting,
    currentDraftId,
    activeDraftId,
    streamingToDraft,
    draftStreamingContent,
    
    // Refs
    draftContentRef,
    draftIdRef,
    streamingToDraftRef,
    
    // Setters
    setAutoDrafting,
    setActiveDraftId,
    setCurrentDraftId,
    
    // Actions
    isDocumentRequest,
    isDraftingAgent,
    shouldAutoDraft,
    resetDraftStreaming,
    startDraftStreaming,
    appendDraftContent,
    finalizeDraft,
    toggleAutoDrafting
  };
};

export default useAutoDrafting;
